import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPLabel, IPText } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import TextInfo from 'components/Notification/Info';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import TimePicker from 'components/Pickers/TimePicker';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalTextStylesBold,
  globalTextStylesError,
} from 'globalStyles';
import Task from 'models/tasks/task';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setNewDateKeepTime,
  setNewTimeKeepDate,
  toLocaleDateLong,
  toLocaleDateTimeMedium,
  toLocaleTimeNoSeconds,
} from 'utils/datetime';
import { getTaskDeadlineLabel } from './taskDetailRules';
import { useIsInTaskDetailCallout } from './TaskDetailRow';
import { dropdownStyles, durationValueOptions, iconFontSize, readOnlyRowSpace } from './TaskDetailStyles';

interface ITaskDetailDeadlineProps {
  orgTask: Task;
  task: Task;
  canUpdate: boolean;
  onUpdate: (newTask: Task) => void;
  getErrorMessage: (field: string) => string | undefined;
  simple?: boolean; //hides the calculated working hours, for the compact create wizard
}

const getUnit = (n?: number): 'hours' | 'days' => {
  if (!n) return 'hours';
  if (n < 24) return 'hours';

  return 'days';
};

export const TaskDetailDeadline = (props: ITaskDetailDeadlineProps) => {
  const isInCallout = useIsInTaskDetailCallout();
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'translation']);
  const theme = getTheme();
  const [timeUnit, setTimeUnit] = useState<string>(getUnit(props.task.duration));
  const [timeValue, setTimeValue] = useState<number>(props.task.duration);
  const isReadonly = !canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Deadline, appContext);

  useEffect(() => {
    if (timeUnit === 'hours') {
      setTimeValue(props.task.duration);
    } else {
      setTimeValue(Math.floor(props.task.duration / 8));
    }
  }, [props.task.duration, timeUnit]);

  const durationTimeOptions: IPSelectableOption[] = [
    {
      key: 'hours',
      text: t('task:Repeat.Hours'),
    },
    {
      key: 'days',
      text: t('task:Repeat.Days'),
    },
  ];

  //
  // Main render
  //
  return (
    <IPStack
      horizontal
      tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
      verticalAlign="start"
      styles={isReadonly ? { root: { paddingBottom: readOnlyRowSpace } } : undefined}
    >
      {!isInCallout && (
        <IPStack.Item>
          {!appContext.isMobileView && <IPFontIcon iconName="InfoSolid" style={iconFontSize} />}
        </IPStack.Item>
      )}
      {props.task.hasComputedDeadline() && (
        <IPStack.Item grow>
          <IPStack
            horizontal
            wrap={isInCallout}
            tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
            verticalAlign="end"
          >
            <IPStack.Item>
              <IPDropdown
                options={durationValueOptions}
                styles={dropdownStyles}
                placeholder={t('task:Repeat.HourDropDownPlaceholder')}
                selectedKey={timeValue}
                onChange={(event: React.FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                  if (!option) return;
                  const newTimeValue = option.key as number;

                  if (newTimeValue === timeValue) return;
                  setTimeValue(newTimeValue);

                  const copyTask = props.task.clone();
                  if (timeUnit === 'hours') {
                    copyTask.duration = option.key as number;
                  } else {
                    copyTask.duration = (option.key as number) * 8;
                  }
                  props.onUpdate(copyTask);
                }}
                disabled={isReadonly}
                info={t('task:Deadline.MaximumDurationInfo')}
                label={t('task:Deadline.MaximumDuration')}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPDropdown
                options={durationTimeOptions}
                styles={dropdownStyles}
                selectedKey={timeUnit}
                onChange={(event: React.FormEvent<HTMLDivElement>, option?: IPSelectableOption, index?: number) => {
                  if (!option) return;
                  const newTimeUnit = option?.key.toString();

                  // Return if nothing has changed.
                  if (newTimeUnit === timeUnit) return;
                  setTimeUnit(newTimeUnit);

                  const copyTask = props.task.clone();
                  if (newTimeUnit !== 'hours') {
                    copyTask.duration = props.task.duration * 8;
                  } else {
                    copyTask.duration = Math.floor(props.task.duration / 8);
                  }
                  props.onUpdate(copyTask);
                }}
                disabled={isReadonly}
              />
            </IPStack.Item>
            {props.task.isInstance() && (
              <IPStack.Item>
                <IPTextField
                  label={getTaskDeadlineLabel(props.task, t)}
                  styles={{
                    fieldGroup: {
                      background: appContext.useDarkMode ? theme.palette.themeDarkAlt : theme.palette.themeLighterAlt,
                    },
                  }}
                  readOnly
                  value={toLocaleDateTimeMedium(props.task.getDeadline(appContext.globalDataCache))}
                ></IPTextField>
              </IPStack.Item>
            )}
            {timeUnit !== 'hours' && (
              <IPStack.Item>
                <IPTextField
                  label={t('task:Deadline.CalcHours')}
                  styles={{
                    fieldGroup: {
                      background: appContext.useDarkMode ? theme.palette.themeDarkAlt : theme.palette.themeLighterAlt,
                    },
                    root: { width: 80 },
                  }}
                  readOnly
                  value={props.task.duration.toLocaleString()}
                ></IPTextField>
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
      )}
      {!props.task.hasComputedDeadline() && (
        <IPStack.Item grow>
          <IPStack
            horizontal
            //in a callout the date gets a row of its own, the time and the hours follow on the next one
            wrap={isInCallout}
            tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
            verticalAlign="start"
          >
            {isReadonly && (
              <Fragment>
                <IPStack.Item grow>
                  <IPLabel>{getTaskDeadlineLabel(props.task, t)}</IPLabel>
                  <IPText>{`${toLocaleDateLong(props.task.deadline)}, ${toLocaleTimeNoSeconds(
                    props.task.deadline,
                  )}`}</IPText>
                </IPStack.Item>
              </Fragment>
            )}
            {!isReadonly && (
              <Fragment>
                <IPStack.Item grow styles={isInCallout ? { root: { width: '100%' } } : undefined}>
                  <LocalizedDatePicker
                    label={getTaskDeadlineLabel(props.task, t)}
                    showMonthPickerAsOverlay={false}
                    showWeekNumbers={true}
                    value={props.task.deadline}
                    minDate={props.task.endDateTime}
                    onDateChange={(date: Date | undefined) => {
                      if (!date) return;
                      const copyTask = props.task.clone();
                      copyTask.deadline = setNewDateKeepTime(date, copyTask.deadline);
                      props.onUpdate(copyTask);
                    }}
                  />
                  <IPText variant="small" styles={globalTextStylesError}>
                    {props.getErrorMessage('deadline')}
                  </IPText>
                </IPStack.Item>
                <IPStack.Item>
                  <TimePicker
                    selectedTime={props.task.deadline}
                    label={t('task:EndTime.Label')}
                    onUpdateTime={(hours: number, mins: number) => {
                      const copyTask = props.task.clone();
                      copyTask.deadline = setNewTimeKeepDate(copyTask.deadline, hours, mins);
                      props.onUpdate(copyTask);
                    }}
                  />
                </IPStack.Item>
                {!props.simple && (
                  <IPStack.Item>
                    <TextInfo message={t('task:Deadline.CalcHoursInfo')}>
                      <IPLabel styles={globalTextStylesBold}>{t('task:Deadline.CalcHours')}</IPLabel>
                    </TextInfo>
                    <IPTextField
                      styles={{
                        fieldGroup: {
                          background: appContext.useDarkMode
                            ? theme.palette.themeDarkAlt
                            : theme.palette.themeLighterAlt,
                        },
                        root: { width: 100 },
                      }}
                      readOnly
                      value={props.task
                        .getWorkingHoursBetween(
                          props.task.deadline,
                          props.task.startDateTime,
                          appContext.globalDataCache,
                        )
                        .toLocaleString()}
                    />
                  </IPStack.Item>
                )}
              </Fragment>
            )}
          </IPStack>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
